import { useState, type SyntheticEvent } from 'react';
import { Navigate, useLocation } from 'react-router';

import { LoginFailedError } from '@/auth/session';
import { useAuth } from '@/auth/useAuth';
import { useModules } from '@/modules/ModuleContext';
import { landingPath } from '@/shell/landing';
import { Button, FormField, Icon, TextInput, Wordmark } from '@/ui';

interface RedirectState {
  readonly from?: string;
}

/**
 * The sign-in screen.
 *
 * The workspace field appears only once the server has asked for it. Most
 * contractors have exactly one workspace, so showing the field to everyone
 * would make the common case harder in order to serve the rare one; the server
 * tells us which case we are in, and the form adapts.
 *
 * This is the only screen outside the application shell, so it carries the mark
 * itself. It is deliberately the quietest page in the product.
 *
 * Signing in returns a person to the page they were turned away from. With nowhere in particular
 * to return to, they land where their work is: Field for technicians, Today for everyone else.
 *
 * @returns the sign-in form, or a redirect when a session already exists
 */
export function LoginPage(): React.JSX.Element {
  const { status, signIn, can } = useAuth();
  const enabled = useModules();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [workspaceRequired, setWorkspaceRequired] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'authenticated') {
    const from = (location.state as RedirectState | null)?.from;
    // Turned away from the home page counts as having nowhere to return to.
    const destination = from === undefined || from === '/' ? landingPath({ can, enabled }) : from;
    return <Navigate to={destination} replace />;
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await signIn(email, password, workspaceRequired ? workspace : undefined);
    } catch (caught) {
      if (caught instanceof LoginFailedError) {
        if (caught.kind === 'WORKSPACE_REQUIRED') {
          setWorkspaceRequired(true);
        }
        setError(caught.message);
      } else {
        setError('Sign-in is temporarily unavailable. Try again in a moment.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="fo-signin">
      <section className="fo-signin__story" aria-label="FieldOps workspace">
        <img
          className="fo-signin__art"
          src="/images/clear/operations-sculpture.png"
          alt=""
          width="1122"
          height="1402"
          fetchPriority="high"
        />
        <div className="fo-signin__story-brand">
          <Wordmark />
          <span>One connected workspace.</span>
        </div>
        <div className="fo-signin__story-copy">
          <p className="fo-signin__eyebrow">Your work, in sync</p>
          <h2>One board for the trucks, the calls, and the money.</h2>
          <p>
            From the first call to the final invoice. A clear view of the day, with the power to
            keep it moving.
          </p>
        </div>
        <div className="fo-signin__story-footer">
          <span>Built for the people who keep things running.</span>
          <Icon name="dispatch" size="1.25rem" />
        </div>
      </section>
      <div className="fo-signin__panel">
        <div className="fo-signin__brand">
          <Wordmark />
        </div>

        <div className="fo-signin__card">
          <h1 className="fo-signin__title">Sign in to FieldOps CRM</h1>
          <p className="fo-signin__lede">Welcome back. Let’s get your day moving.</p>

          <form
            className="fo-signin__form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
            noValidate
          >
            <FormField label="Email address or username" required>
              <TextInput
                name="email"
                type="text"
                autoComplete="username"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                }}
              />
            </FormField>

            <FormField label="Password" required>
              <TextInput
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                }}
              />
            </FormField>

            {workspaceRequired ? (
              <FormField label="Workspace" required hint="More than one workspace uses this login.">
                <TextInput
                  name="workspace"
                  type="text"
                  autoComplete="organization"
                  value={workspace}
                  onChange={(event) => {
                    setWorkspace(event.target.value);
                  }}
                />
              </FormField>
            ) : null}

            {/* Announced to assistive technology the moment it appears, so a
                screen-reader user is not left waiting on a form that has
                silently rejected them. */}
            {error !== null ? (
              <p className="fo-signin__error" role="alert">
                {error}
              </p>
            ) : null}

            <Button type="submit" variant="primary" block disabled={submitting}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        <p className="fo-signin__footnote">
          Trouble signing in? Ask an owner in your workspace to check your account.
        </p>
        <div className="fo-signin__trust">
          <Icon name="lock" size="0.875rem" />
          <span>Your workspace. Your team. Your business.</span>
        </div>
      </div>
    </main>
  );
}
